import { message } from "antd";
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosError } from "axios";
import { getApiBaseUrl } from "./WebConfig";
import { showToast } from "../../common/Toast";
import { compile } from "sass";
class Network {
  private axiosInstance: AxiosInstance;
  private tokenListeners: (() => void)[] = [];
  private generateCurlCommand(config: any): string {
    const method = config.method.toUpperCase();
    const url = `${config.baseURL}${config.url}`.replace(/([^:]\/)\/+/g, "$1");
    const headers = config.headers || {};
    const data = config.data;

    let curl = `\nEquivalent curl command:\ncurl --location '${url}'`;

    // 添加 headers
    Object.entries(headers).forEach(([key, value]) => {
      if (key !== 'skipAuthInterceptor') {  // 跳过内部使用的 header
        curl += `\n--header '${key}: ${value}'`;
      }
    });

    // 添加 data
    if (data) {
      curl += `\n--data '${typeof data === 'string' ? data : JSON.stringify(data, null, 2)}'`;
    }

    return curl;
  }

  constructor(baseURL: string) {
    const origin = window.location.origin; // 获取当前源
    console.log('Current origin:', origin, baseURL);

    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000, // 设置超时时间
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
        "Accept": "*/*",
      }
    });
    // 请求拦截器
    this.axiosInstance.interceptors.request.use(
      (config: any) => {
        // 打印请求的curl命令
        // console.log(this.generateCurlCommand(config));
        // 如果数据是 FormData，不要尝试 JSON.stringify
        if (config.data instanceof FormData) {
          config.headers['Content-Type'] = 'multipart/form-data';
        } else {
          config.headers['Content-Type'] = 'application/json';
        }
        // 检查是否需要跳过认证
        const token = localStorage.getItem('token');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config as InternalAxiosRequestConfig;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );
    // 响应拦截器
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 可以在这里处理通用的响应逻辑
        // console.log('response',response);
        return response.data;
      },
      (error: AxiosError) => {
        console.error('Network Error Details:', {
          message: error.message,
          config: error.config,
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: error.response?.headers,
          data: error.response?.data
        });
        if (error.response?.status === 401) {
          message.error("Your login status has expired. Please log in again");
          window.location.href = "/login";
          // this.tokenListeners.forEach(listener => listener());
          localStorage.removeItem('token');
          localStorage.removeItem('id');
        }
        if (error.stack) {
          console.error('Network Error Stack:', error.stack);
        } else {
          console.error('Network Error:', error);
        }
        if(error.message.includes('timeout')){
          // showToast(error.message); // 使用全局方法显示错误信息
          showToast('Network request failed')
        }
        return Promise.reject(error?.response?.data || error);
      }
    );
  }
  // 设置请求路径
  public refreshEnvironment() {
    this.axiosInstance.defaults.baseURL = getApiBaseUrl();
  }
  // 添加 token 过期监听器
  public addTokenListener(listener: () => void) {
    this.tokenListeners.push(listener);
  }

  public removeTokenListener(listener: () => void) {
    this.tokenListeners = this.tokenListeners.filter(l => l !== listener);
  }

  // GET 请求
  public get<T>(url: string, params?: Record<string, any>): Promise<T> {
    return this.axiosInstance.get(url, { params });
  }

  // POST 请求
  public post<T>(url: string, data?: Record<string, any>): Promise<T> {
    return this.axiosInstance.post(url, data);
  }

  // PUT 请求
  public put<T>(url: string, data?: Record<string, any>): Promise<T> {
    return this.axiosInstance.put(url, data);
  }

  // DELETE 请求
  public delete<T>(url: string, params?: Record<string, any>): Promise<T> {
    return this.axiosInstance.delete(url, { params });
  }
  // 导出Excel GET请求
  public exportExcelGet<T>(url: string, data?: Record<string, any>): Promise<T> {
    return this.axiosInstance.get(url, { responseType: 'blob', params: data });
  }
  
  // 导出Excel POST请求
  public exportExcelPost<T>(url: string, data?: Record<string, any>): Promise<T> {
    return this.axiosInstance.post(url, data, { responseType: 'blob', });
  }
  // 上传文件
  public uploadFile<T>(url: string, data: FormData): Promise<T> {
    return this.axiosInstance.post(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
}

export default new Network(getApiBaseUrl()); // 替换为你的 API 基础 URL 
